import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Mail, Lock, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import authService from '@/services/authService';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [mode, setMode] = useState<'request' | 'reset' | 'success'>('request');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // Check if we have an access token in the URL (from email link)
    const accessToken = searchParams.get('access_token');
    const refreshToken = searchParams.get('refresh_token');
    
    if (accessToken && refreshToken) {
      setMode('reset');
    }
  }, [searchParams]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string) => {
    // Strong password validation
    return password.length >= 8 && 
           /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(password);
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({ email: '', password: '', confirmPassword: '' });

    // Validation
    if (!validateEmail(email)) {
      setErrors(prev => ({ ...prev, email: t('invalidEmail') }));
      setIsLoading(false);
      return;
    }

    try {
      const { error } = await authService.resetPassword(email);

      if (error) {
        throw error;
      }

      toast.success(t('resetLinkSuccess'));
      setMode('success');
    } catch (error: any) {
      console.error('Password reset request error:', error);
      toast.error(error.message || t('resetLinkFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({ email: '', password: '', confirmPassword: '' });

    // Validation
    let formIsValid = true;
    
    if (!validatePassword(newPassword)) {
      setErrors(prev => ({ ...prev, password: t('passwordComplexityError') }));
      formIsValid = false;
    }

    if (newPassword !== confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: t('passwordMismatch') }));
      formIsValid = false;
    }

    if (!formIsValid) {
      setIsLoading(false);
      return;
    }

    try {
      const { error } = await authService.updatePassword(newPassword);

      if (error) {
        throw error;
      }

      toast.success(t('passwordUpdateSuccess'));
      navigate('/login');
    } catch (error: any) {
      console.error('Password update error:', error);
      toast.error(error.message || t('passwordUpdateFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const renderRequestForm = () => (
    <Card className="w-full max-w-md bg-slate-800/80 backdrop-blur-lg border-slate-700 shadow-2xl">
      <CardHeader className="text-center space-y-2">
        <CardTitle className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
          {t('resetPassword')}
        </CardTitle>
        <CardDescription className="text-slate-300">
          {t('resetPasswordDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleRequestReset} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-300 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {t('emailAddress')}
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-purple-400 focus:ring-purple-400/20"
              placeholder={t('enterEmail')}
              required
            />
            {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
          </div>

          <Button 
            type="submit" 
            disabled={isLoading} 
            className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white font-semibold py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('sendingResetLink')}
              </>
            ) : (
              t('sendResetLink')
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-purple-400 hover:text-purple-300 underline">
            {t('backToLogin')}
          </Link>
        </div>
      </CardContent>
    </Card>
  );

  const renderResetForm = () => (
    <Card className="w-full max-w-md bg-slate-800/80 backdrop-blur-lg border-slate-700 shadow-2xl">
      <CardHeader className="text-center space-y-2">
        <CardTitle className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
          {t('setNewPassword')}
        </CardTitle>
        <CardDescription className="text-slate-300">
          {t('enterNewPassword')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handlePasswordUpdate} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="newPassword" className="text-slate-300 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              {t('newPassword')}
            </Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-purple-400 focus:ring-purple-400/20 pr-10"
                placeholder={t('enterNewPasswordPlaceholder')}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-300"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && <p className="text-red-400 text-sm mt-1">{errors.password}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-slate-300 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              {t('confirmNewPassword')}
            </Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-purple-400 focus:ring-purple-400/20 pr-10"
                placeholder={t('reenterNewPassword')}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-300"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirmPassword && <p className="text-red-400 text-sm mt-1">{errors.confirmPassword}</p>}
          </div>

          {/* Password Requirements */}
          <div className="text-xs text-slate-400 bg-slate-700/30 p-3 rounded">
            <p className="font-medium mb-1">{t('passwordRequirements')}</p>
            <ul className="list-disc list-inside space-y-1">
              <li>{t('atLeast8Chars')}</li>
              <li>{t('upperCase')}</li>
              <li>{t('lowerCase')}</li>
              <li>{t('numbers')}</li>
              <li>{t('specialChars')}</li>
            </ul>
          </div>

          <Button 
            type="submit" 
            disabled={isLoading} 
            className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white font-semibold py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('updatingPassword')}
              </>
            ) : (
              t('updatePassword')
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );

  const renderSuccessMessage = () => (
    <Card className="w-full max-w-md bg-slate-800/80 backdrop-blur-lg border-slate-700 shadow-2xl">
      <CardHeader className="text-center space-y-2">
        <div className="mx-auto w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="w-6 h-6 text-green-400" />
        </div>
        <CardTitle className="text-2xl font-bold text-white">
          {t('resetLinkSent')}
        </CardTitle>
        <CardDescription className="text-slate-300">
          {t('resetLinkSentDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent className="text-center space-y-4">
        <div className="text-sm text-slate-400 bg-slate-700/30 p-4 rounded">
          <p className="font-medium mb-2">{t('followSteps')}</p>
          <ol className="text-left list-decimal list-inside space-y-1">
            <li>{t('checkInbox')}</li>
            <li>{t('clickResetLink')}</li>
            <li>{t('setNewPasswordStep')}</li>
            <li>{t('loginWithNewPassword')}</li>
          </ol>
        </div>
        
        <p className="text-xs text-slate-400">
          {t('noEmailReceived')}
          <button 
            onClick={() => setMode('request')}
            className="text-purple-400 hover:text-purple-300 underline ml-1"
          >
            {t('resendLink')}
          </button>
        </p>

        <Button 
          onClick={() => navigate('/login')}
          variant="outline"
          className="w-full bg-slate-700/50 border-slate-600 text-slate-300 hover:bg-slate-600/50"
        >
          {t('backToLogin')}
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-900">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>

      {/* Back to Login */}
      <div className="absolute top-4 left-4 z-20">
        <Link to="/login">
          <Button variant="outline" size="sm" className="bg-slate-800/50 border-slate-600 text-slate-300 hover:bg-slate-700/50">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('backToLogin')}
          </Button>
        </Link>
      </div>

      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900">
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
        }}></div>
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="animate-pulse absolute top-20 left-20 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl"></div>
          <div className="animate-pulse absolute bottom-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animation-delay-2000"></div>
          <div className="animate-pulse absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animation-delay-4000"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        {mode === 'request' && renderRequestForm()}
        {mode === 'reset' && renderResetForm()}
        {mode === 'success' && renderSuccessMessage()}
      </div>
    </div>
  );
};

export default ResetPassword;
